import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, Mail, Lock, ArrowRight, AlertCircle, Eye, EyeOff, Activity, Cpu, Database, Globe, Zap, Lock as LockIcon, Server } from "lucide-react";
import { loginAdmin } from "@/shared/api/auth";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

function LiveClock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => { const i = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(i); }, []);
  return (
    <span className="font-mono text-xs tracking-widest text-blue-400/50">
      {time.toLocaleTimeString("en-US", { hour12: false })}
    </span>
  );
}

/* Animated cyber grid */
function CyberGrid() {
  return (
    <div 
      className="absolute inset-0 pointer-events-none"
      style={{
        backgroundImage: `
          linear-gradient(rgba(59,130,246,0.04) 1px, transparent 1px),
          linear-gradient(90deg, rgba(59,130,246,0.04) 1px, transparent 1px)
        `,
        backgroundSize: "48px 48px",
        maskImage: "radial-gradient(ellipse 90% 90% at 50% 50%, black 20%, transparent 100%)",
        WebkitMaskImage: "radial-gradient(ellipse 90% 90% at 50% 50%, black 20%, transparent 100%)",
      }} 
    />
  );
}

/* Scan line effect */
function ScanLine() {
  return (
    <motion.div
      className="absolute left-0 right-0 h-0.5 pointer-events-none z-10"
      style={{
        background: "linear-gradient(90deg, transparent, rgba(59,130,246,0.3), rgba(96,165,250,0.5), rgba(59,130,246,0.3), transparent)",
        filter: "blur(1px)",
      }}
      animate={{ top: ["0%", "100%"] }}
      transition={{ duration: 5, repeat: Infinity, ease: "linear", repeatDelay: 3 }}
    />
  );
}

/* Floating data nodes */
function DataNodes() {
  const nodes = useRef(
    Array.from({ length: 12 }, (_, i) => ({
      id: i,
      x: 10 + Math.random() * 80,
      y: 10 + Math.random() * 80,
      size: Math.random() * 4 + 2,
      duration: Math.random() * 10 + 8,
      delay: Math.random() * 6,
    }))
  ).current;

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {nodes.map((n) => (
        <motion.div
          key={n.id}
          className="absolute rounded-full"
          style={{
            left: `${n.x}%`, 
            top: `${n.y}%`,
            width: n.size, 
            height: n.size,
            background: "rgba(96,165,250,0.5)",
            boxShadow: "0 0 6px rgba(96,165,250,0.5)",
          }}
          animate={{
            opacity: [0, 0.8, 0],
            scale: [0.5, 1.2, 0.5],
          }}
          transition={{ duration: n.duration, delay: n.delay, repeat: Infinity }}
        />
      ))}
    </div>
  );
}

/* Loading stage indicator */
function LoadingStages({ stage }) {
  const stages = ["Connecting", "Authenticating", "Authorizing", "Granting Access"];
  return (
    <div className="w-full mb-6">
      <div className="flex justify-between mb-2">
        {stages.map((s, i) => (
          <div key={s} className="flex flex-col items-center gap-1.5">
            <motion.div
              className="rounded-full"
              style={{
                width: 8, height: 8,
                background: i < stage ? "#3B82F6" : "rgba(59,130,246,0.15)",
                boxShadow: i < stage ? "0 0 10px rgba(59,130,246,0.8)" : "none",
              }}
              animate={i < stage ? { scale: [1, 1.4, 1] } : {}}
              transition={{ duration: 0.5, repeat: i === stage - 1 ? Infinity : 0 }}
            />
            {i < stages.length - 1 && (
              <div 
                className="absolute"
                style={{
                  width: `${100 / stages.length}%`,
                  height: 1,
                  background: i < stage - 1 ? "#3B82F6" : "rgba(59,130,246,0.1)",
                }} 
              />
            )}
          </div>
        ))}
      </div>
      <div className="relative h-0.5 bg-blue-500/10 rounded-full overflow-hidden">
        <motion.div
          className="h-full bg-gradient-to-r from-blue-700 via-blue-500 to-blue-300 rounded-full"
          animate={{ width: `${(stage / stages.length) * 100}%` }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        />
        <motion.div
          className="absolute top-0 left-0 bottom-0 w-2/5"
          style={{
            background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
          }}
          animate={{ x: ["-100%", "300%"] }}
          transition={{ duration: 1.2, repeat: stage > 0 && stage < 4 ? Infinity : 0, ease: "easeInOut" }}
        />
      </div>
      <motion.p
        key={stage}
        initial={{ opacity: 0, y: 1 }}
        animate={{ opacity: 1, y: 0 }}
        className="font-mono text-xs text-blue-400/50 mt-2 tracking-wider"
      >
        {stage > 0 ? `> ${stages[Math.min(stage - 1, 3)]}...` : "> Idle"}
      </motion.p>
    </div>
  );
}

/* Features list */
const FEATURES = [
  { icon: Cpu, label: "Real-time transaction monitoring", color: "#60A5FA" },
  { icon: Database, label: "Merchant & reseller management", color: "#34D399" },
  { icon: Activity, label: "Advanced analytics & reporting", color: "#A78BFA" },
  { icon: Server, label: "System configuration & audit logs", color: "#FCD34D" },
  { icon: Globe, label: "Multi-region payment routing", color: "#FB7185" },
  { icon: LockIcon, label: "Security & compliance center", color: "#38BDF8" },
];

export default function AdminLogin() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [focused, setFocused] = useState("");
  const [stage, setStage] = useState(0);
  const [success, setSuccess] = useState(false);
  const containerRef = useRef(null);
  const [mouse, setMouse] = useState({ x: 50, y: 50 });

  useEffect(() => {
    const h = (e) => {
      if (!containerRef.current) return;
      const r = containerRef.current.getBoundingClientRect();
      setMouse({ x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
    };
    window.addEventListener("mousemove", h);
    return () => window.removeEventListener("mousemove", h);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) { setError("Please enter both email and password"); return; }
    setLoading(true);
    // Animated stages
    const advance = (s) => new Promise(res => setTimeout(() => { setStage(s); res(); }, 650));
    try {
      setStage(1);
      await advance(2);
      await loginAdmin(email, password);
      await advance(3);
      await advance(4);
      setSuccess(true);
      setTimeout(() => navigate("/AdminDashboard"), 1000);
    } catch (err) {
      setError(err.message || "Authentication failed");
      setLoading(false); setStage(0);
    }
  };

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-slate-950 flex font-sans relative overflow-hidden"
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600&family=Playfair+Display:wght@700;800&display=swap');
        * { box-sizing: border-box; }

        .adm2-input {
          width: 100%; height: 54px;
          background: rgba(10,22,50,0.85);
          border: 1.5px solid rgba(59,130,246,0.16);
          border-radius: 14px;
          color: #DBEAFE;
          font-size: 14px; font-family: 'Outfit', sans-serif; font-weight: 500;
          padding: 0 48px;
          outline: none;
          caret-color: #3B82F6;
          transition: all 0.28s cubic-bezier(0.4,0,0.2,1);
          letter-spacing: 0.01em;
        }
        .adm2-input::placeholder { color: rgba(96,165,250,0.22); }
        .adm2-input:hover { border-color: rgba(59,130,246,0.3); background: rgba(10,26,58,0.9); }
        .adm2-input:focus {
          border-color: rgba(59,130,246,0.65);
          background: rgba(10,30,70,0.95);
          box-shadow: 0 0 0 4px rgba(59,130,246,0.1), 0 4px 20px rgba(59,130,246,0.08);
        }

        .adm2-btn {
          width: 100%; height: 54px;
          background: linear-gradient(135deg, #1D4ED8 0%, #2563EB 45%, #3B82F6 100%);
          border: none; border-radius: 14px;
          color: white; font-family: 'Outfit', sans-serif;
          font-size: 15px; font-weight: 700; letter-spacing: 0.02em;
          cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 9px;
          position: relative; overflow: hidden;
          box-shadow: 0 6px 28px rgba(37,99,235,0.4), inset 0 1px 0 rgba(255,255,255,0.12);
          transition: all 0.3s cubic-bezier(0.4,0,0.2,1);
        }
        .adm2-btn:not(:disabled):hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 36px rgba(37,99,235,0.55), inset 0 1px 0 rgba(255,255,255,0.12);
          background: linear-gradient(135deg, #2563EB 0%, #3B82F6 45%, #60A5FA 100%);
        }
        .adm2-btn:not(:disabled):active { transform: translateY(0) scale(0.99); }
        .adm2-btn:disabled { opacity: 0.6; cursor: not-allowed; }

        .adm2-shimmer {
          position: absolute; inset: 0;
          background: linear-gradient(105deg, transparent 35%, rgba(255,255,255,0.15) 50%, transparent 65%);
          transform: translateX(-100%); transition: transform 0.6s ease;
        }
        .adm2-btn:not(:disabled):hover .adm2-shimmer { transform: translateX(100%); }

        .adm2-spin {
          width: 20px; height: 20px;
          border: 2.5px solid rgba(255,255,255,0.3);
          border-top-color: white; border-radius: 50%;
          animation: adm2rot 0.7s linear infinite;
        }
        @keyframes adm2rot { to { transform: rotate(360deg); } }

        .adm2-feature {
          display: flex; align-items: center; gap: 12px;
          padding: 9px 0;
          border-bottom: 1px solid rgba(59,130,246,0.06);
          transition: transform 0.2s, opacity 0.2s;
          cursor: default;
        }
        .adm2-feature:last-child { border-bottom: none; }
        .adm2-feature:hover { transform: translateX(5px); }

        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus {
          -webkit-box-shadow: 0 0 0 1000px rgba(8,18,44,0.95) inset !important;
          -webkit-text-fill-color: #DBEAFE !important;
        }
      `}</style>

      {/* Background effects */}
      <div 
        className="fixed w-96 h-96 lg:w-[700px] lg:h-[700px] rounded-full pointer-events-none z-0"
        style={{
          background: "radial-gradient(circle, rgba(37,99,235,0.13) 0%, transparent 65%)",
          top: `${mouse.y * 0.2 - 8}%`, 
          left: `${mouse.x * 0.2 - 8}%`,
          filter: "blur(70px)",
          transition: "all 1.6s cubic-bezier(0.23,1,0.32,1)",
        }} 
      />
      <div 
        className="fixed w-80 h-80 lg:w-[500px] lg:h-[500px] rounded-full pointer-events-none z-0"
        style={{
          background: "radial-gradient(circle, rgba(96,165,250,0.07) 0%, transparent 65%)",
          bottom: "5%", 
          right: "8%",
          filter: "blur(80px)",
        }} 
      />

      {/* Top status bar */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="fixed top-0 left-0 right-0 h-12 flex items-center justify-between px-6 lg:px-8 bg-slate-950/80 backdrop-blur-2xl border-b border-blue-500/10 z-50"
      >
        <div className="flex items-center gap-2.5">
          <motion.div
            className="w-2 h-2 rounded-full bg-green-400 shadow-lg"
            style={{ boxShadow: "0 0 10px rgb(74, 222, 128)" }}
            animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }}
            transition={{ duration: 2.2, repeat: Infinity }}
          />
          <span className="font-mono text-xs text-blue-400/40 tracking-widest uppercase">
            System Operational
          </span>
        </div>
        <div className="flex items-center gap-5">
          <div className="flex items-center gap-1.5">
            <Activity size={11} color="rgba(96,165,250,0.3)" />
            <span className="font-mono text-xs text-blue-400/25 tracking-wider">v4.2.1</span>
          </div>
          <LiveClock />
        </div>
      </motion.div>

      {/* ─── MAIN LAYOUT ─── */}
      <div className="flex flex-col lg:flex-row w-full max-w-6xl mx-auto items-center gap-8 lg:gap-16 xl:gap-8 md:pt-12 p-4 sm:p-6 lg:p-12 pt-10 lg:pt-12 relative z-10">

        {/* LEFT PANEL */}
        <motion.div
          className="flex-1 w-full p-6"
          initial={{ opacity: 0, x: -32 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Logo */}
          <motion.div
            className="flex items-center gap-3.5 mb-8 md:mb-12"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <motion.div
              whileHover={{ scale: 1.1, rotate: -6 }}
              transition={{ type: "spring", stiffness: 400, damping: 15 }}
              className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-900 via-blue-700 to-blue-500 flex items-center justify-center shadow-2xl shadow-blue-900/50 relative overflow-hidden"
            >
              <img src="/Media.png" alt="1401 Logo" className="w-full h-full object-cover" />
            </motion.div>
            <div>
              <div className="font-serif font-black text-2xl text-blue-50 tracking-tight leading-none">1401</div>
              <div className="font-mono text-xs text-blue-300/40 tracking-widest mt-1 uppercase">Payment Gateway</div>
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.3 }}
            className="mb-10"
          >
            <h1 className="font-serif font-black text-3xl md:text-4xl text-blue-50 leading-tight mb-4 tracking-tight">
              Admin<br />
              <span className="bg-gradient-to-r text-3xl md:text-4xl from-blue-400 via-blue-300 to-cyan-300 bg-clip-text text-transparent">
                Control Center
              </span>
            </h1>
            <p className="text-sm md:text-base text-blue-300/45 leading-relaxed w-sm md:w-full font-light">
              Full administrative access to manage the entire payment infrastructure with enterprise-grade security.
            </p>
          </motion.div>

          {/* Features */}
          <div className="mb-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-4">
            {FEATURES.map((f, i) => (
              <motion.div
                key={f.label}
                className="adm2-feature"
                initial={{ opacity: 0, x: -4 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 + i * 0.07 }}
              >
                <div 
                  className="w-9 h-9 rounded-lg flex-shrink-0 flex items-center justify-center"
                  style={{
                    background: `${f.color}18`,
                    border: `1px solid ${f.color}30`,
                    boxShadow: `0 0 12px ${f.color}22`,
                  }}
                >
                  <f.icon size={16} color={f.color} />
                </div>
                <span className="text-xs md:text-sm text-blue-200/55 font-medium">{f.label}</span>
              </motion.div>
            ))}
          </div>

          {/* Trust tags */}
          <motion.div
            className="flex flex-wrap gap-2"
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            transition={{ delay: 0.85 }}
          >
            {["PCI DSS", "ISO 27001", "256-bit SSL", "SOC 2"].map((tag) => (
              <span 
                key={tag} 
                className="px-2 sm:px-3 py-1 rounded-lg bg-blue-500/7 border border-blue-500/16 font-mono text-[10px] sm:text-xs text-blue-300/38 font-semibold"
              >
                {tag}
              </span>
            ))}
          </motion.div>
        </motion.div>

        {/* RIGHT FORM */}
        <motion.div
          className="w-full md:w-full lg:w-[440px] flex-shrink-0"
          initial={{ opacity: 0, y: 7 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1], delay: 0.12 }}
        >
          <div className=" bg-gradient-to-br from-slate-900/97 to-slate-950/99 border border-blue-500/20 rounded-3xl p-6 md:p-10 shadow-2xl shadow-black/50 relative overflow-hidden">
            <CyberGrid />
            <ScanLine />
            <DataNodes />

            {/* Top shimmer */}
            <div 
              className="absolute top-0 left-0 right-0 h-px"
              style={{
                background: "linear-gradient(90deg, transparent, rgba(59,130,246,0.8) 35%, rgba(96,165,250,0.8) 65%, transparent)",
              }}
            />

            {/* Card header */}
            <div className="mb-7 relative z-10">
              <motion.div
                whileHover={{ scale: 1.06, rotate: 5 }}
                transition={{ type: "spring", stiffness: 380, damping: 14 }}
                className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-900 via-blue-700 to-blue-500 flex items-center justify-center mb-5 shadow-xl shadow-blue-900/50 relative overflow-hidden cursor-default"
              >
                <img src="/Media.png" alt="1401 Logo" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "inherit" }} />
              </motion.div>

              <h2 className="font-serif font-black text-2xl text-blue-50 mb-0 tracking-tight">
                Administrator Login
              </h2>
              <p className="text-[10px] md:text-xs text-blue-300/38 mt-1 font-normal">
                Secure access · Authorized personnel only
              </p>
            </div>

            <form onSubmit={handleLogin} className="flex flex-col gap-4.5 relative z-10">
              {/* Loading stages */}
              <AnimatePresence>
                {loading && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }} 
                    animate={{ opacity: 1, height: "auto" }} 
                    exit={{ opacity: 0, height: 0 }} 
                    className="overflow-hidden"
                  >
                    <LoadingStages stage={stage} />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Error */}
              <AnimatePresence>
                {error && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0, y: -2 }} 
                    animate={{ opacity: 1, height: "auto", y: 0 }} 
                    exit={{ opacity: 0, height: 0 }} 
                    className="overflow-hidden"
                  >
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-300 text-sm">
                      <AlertCircle size={14} className="flex-shrink-0 text-red-400" />
                      {error}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Success overlay */}
              <AnimatePresence>
                {success && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="absolute inset-0 rounded-3xl bg-slate-950/96 backdrop-blur-sm flex flex-col items-center justify-center gap-4 z-20"
                  >
                    <motion.div
                      initial={{ scale: 0 }} 
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 300, damping: 18 }}
                      className="w-20 h-20 rounded-full bg-blue-500/15 border-2 border-blue-500 flex items-center justify-center shadow-2xl"
                      style={{ boxShadow: "0 0 50px rgba(59,130,246,0.5)" }}
                    >
                      <ShieldCheck size={32} color="#3B82F6" />
                    </motion.div>
                    <div className="font-serif font-black text-xl text-blue-50">Access Granted</div>
                    <div className="font-mono text-[10px] md:text-xs text-blue-400/50 tracking-widest">Redirecting to Admin Dashboard...</div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Email */}
              <div>
                <label className="block text-[10px] md:text-xs font-bold text-blue-400/60 mb-2.5 uppercase tracking-widest">
                  Email Address
                </label>
                <div className="relative">
                  <Mail 
                    size={16} 
                    className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none z-10 transition-colors"
                    style={{
                      color: focused === "email" ? "#3B82F6" : "rgba(96,165,250,0.25)",
                    }}
                  />
                  <input
                    type="email" 
                    className="adm2-input mb-2 pl-12 text-[10px] md:text-xs"
                    placeholder="admin@1401.com"
                    value={email} 
                    onChange={e => setEmail(e.target.value)}
                    onFocus={() => setFocused("email")} 
                    onBlur={() => setFocused("")}
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-[10px] md:text-xs font-bold text-blue-400/60 mb-2.5 uppercase tracking-widest">
                  Password
                </label>
                <div className="relative">
                  <Lock 
                    size={16} 
                    className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none z-10 transition-colors"
                    style={{
                      color: focused === "pass" ? "#3B82F6" : "rgba(96,165,250,0.25)",
                    }}
                  />
                  <input
                    type={showPw ? "text" : "password"} 
                    className="adm2-input pl-12 pr-12 mb-2"
                    placeholder="••••••••"
                    value={password} 
                    onChange={e => setPassword(e.target.value)}
                    onFocus={() => setFocused("pass")} 
                    onBlur={() => setFocused("")}
                    required
                  />
                  <button 
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 bg-transparent border-0 cursor-pointer text-blue-400/30 p-1 rounded transition-colors hover:text-blue-400"
                  >
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Remember + Forgot */}
              <div className="my-2 flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer text-[10px] md:text-xs text-blue-300/40 font-medium hover:text-blue-300/50 transition-colors">
                  <input type="checkbox" className="accent-blue-500" />
                  Remember me
                </label>
                <a 
                  href="#"
                  className="text-[10px] md:text-xs font-semibold text-blue-400 no-underline transition-colors hover:text-blue-300"
                >
                  Forgot password?
                </a>
              </div>

              {/* Submit */}
              <button type="submit" className="adm2-btn mt-1" disabled={loading || success}>
                <div className="adm2-shimmer" />
                <AnimatePresence mode="wait">
                  {success ? (
                    <motion.span 
                      key="done" 
                      initial={{ opacity: 0, scale: 0.8 }} 
                      animate={{ opacity: 1, scale: 1 }}
                      className="flex items-center gap-2 text-[12px] md:text-xs"
                    >
                      <ShieldCheck size={18} /> Access Granted
                    </motion.span>
                  ) : loading ? (
                    <motion.span 
                      key="load" 
                      initial={{ opacity: 0 }} 
                      animate={{ opacity: 1 }}
                      className="flex items-center gap-2 text-[12px] md:text-xs"
                    >
                      <div className="adm2-spin" /> Authenticating...
                    </motion.span>
                  ) : (
                    <motion.span 
                      key="idle" 
                      initial={{ opacity: 0 }} 
                      animate={{ opacity: 1 }}
                      className="flex items-center gap-2 text-[12px] md:text-xs"
                    >
                      Sign in to Admin Panel <ArrowRight size={16} />
                    </motion.span>
                  )}
                </AnimatePresence>
              </button>
            </form>

            <div className="h-px bg-blue-500/10 my-6 relative z-10" />
            <div className="text-center relative z-10">
              <p className="font-mono text-[10px] md:text-xs text-blue-400/22 mb-2.5 tracking-wider">
                ACCESS RESTRICTED TO AUTHORIZED PERSONNEL
              </p>
              <a 
                href="/staff-login"
                className="text-[10px] md:text-xs font-semibold text-gray-400 no-underline transition-colors inline-block py-1.5 px-3 rounded-lg hover:text-blue-300 hover:bg-blue-500/10"
              >
                Staff member? Click here to login →
              </a>
            </div>
          </div>

          {/* Footer links */}
          <div className="flex items-center justify-center gap-4.5 mt-5">
            {["Privacy Policy", "Terms of Service"].map((l, i) => (
              <React.Fragment key={l}>
                {i > 0 && <span className="text-gray-400 text-[10px] md:text-xs">·</span>}
                <a 
                  href="#" 
                  className="text-[10px] md:text-xs text-gray-400 no-underline transition-colors hover:text-blue-400 font-sans"
                >
                  {l}
                </a>
              </React.Fragment>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}